<script lang="ts">
  import type { Object3D } from 'three'
  import { useThrelte } from '@threlte/core'
  import { TransformControls } from '@threlte/extras'
  import { debounce } from 'es-toolkit'
  import { cameraControls } from '../../stores/camera'
  import { sceneMode } from '../../stores/scene-mode'
  import useObjectQueries from '../../routes/project/hooks/useObjectQueries'
  import {
    applyPivotDelta,
    createSelectionPivot,
    snapshotPivotTransform,
    type PivotTransformSnapshot,
  } from '../../helpers/group-transform'
  import { ROTATION_DRAG_SNAP, snapRotationToNearestStep } from '../../helpers/rotation-snap'

  const { objectUuids }: { objectUuids: string[] } = $props()

  const { scene, dom } = useThrelte()
  const { getObject, invalidateCanvas } = useObjectQueries()

  /* svelte-ignore state_referenced_locally */
  const objects = objectUuids
    .map((uuid) => {
      try {
        return getObject(uuid)
      }
      catch {
        return undefined
      }
    })
    .filter((object): object is Object3D => !!object)

  const pivot = createSelectionPivot(objects)

  let snapshot: PivotTransformSnapshot = snapshotPivotTransform(pivot, objects)

  $effect(() => {
    scene.add(pivot)
    return () => {
      scene.remove(pivot)
    }
  })

  const debouncedInvalidateCanvas = debounce(() => {
    invalidateCanvas()
  }, 500)

  function onTransformControlChange() {
    if ($sceneMode === 'rotate') {
      snapRotationToNearestStep(pivot)
    }
    applyPivotDelta(pivot, snapshot, objects)
    debouncedInvalidateCanvas()
  }
</script>

{#if objects.length > 1}
  <TransformControls
    autoPauseOrbitControls
    autoPauseTrackballControls
    mode={$sceneMode === 'rotate' ? 'rotate' : 'translate'}
    rotationSnap={ROTATION_DRAG_SNAP}
    object={pivot}
    onmouseDown={() => {
      snapshot = snapshotPivotTransform(pivot, objects)
      $cameraControls?.disconnect()
    }}
    onmouseUp={() => {
      $cameraControls?.connect(dom)
    }}
    onobjectChange={onTransformControlChange}
  />
{/if}
